import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { memo, useState } from 'react';
import { X } from 'lucide-react';

const FiltersPanel = memo(function FiltersPanel({ filters, setFilters, countriesData, continents, regions, timeZonesData, portsData, totalPorts, visiblePorts }) {
  const [continentSearch, setContinentSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [regionSearch, setRegionSearch] = useState('');
  const [timeZoneSearch, setTimeZoneSearch] = useState('');

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({
      euETS: false,
      fuelEU: false,
      countries: [],
      continents: [],
      regions: [],
      timeZones: [],
      noRegion: false,
      euCountriesOnly: false,
      searchText: '',
      showTimeZones: false,
    });
    setContinentSearch('');
    setCountrySearch('');
    setRegionSearch('');
    setTimeZoneSearch('');
  };

  const addToFilter = (filterKey, value) => {
    setFilters(prev => {
      const currentArray = prev[filterKey] || [];
      if (!currentArray.includes(value)) {
        return { ...prev, [filterKey]: [...currentArray, value] };
      }
      return prev;
    });
  };

  const removeFromFilter = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: prev[filterKey].filter(item => item !== value)
    }));
  };

  return (
    <div className="w-80 bg-white border-r shadow-sm overflow-y-auto">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-2">Filters</h2>
        <p className="text-sm text-gray-500 mb-6">
          Showing {visiblePorts.toLocaleString()} of {totalPorts.toLocaleString()} ports
        </p>

        <div className="space-y-6">
          {/* Search */}
          <div className="space-y-2">
            <Label htmlFor="search">Search Port</Label>
            <Input
              id="search"
              type="text"
              value={filters.searchText}
              onChange={(e) => handleFilterChange('searchText', e.target.value)}
              placeholder="Port name..."
            />
          </div>

          {/* Compliance Filters */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Compliance</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="euETS"
                checked={filters.euETS}
                onCheckedChange={(checked) => handleFilterChange('euETS', checked)}
              />
              <Label htmlFor="euETS" className="text-sm font-normal cursor-pointer">EU ETS Enabled</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="fuelEU"
                checked={filters.fuelEU}
                onCheckedChange={(checked) => handleFilterChange('fuelEU', checked)}
              />
              <Label htmlFor="fuelEU" className="text-sm font-normal cursor-pointer">Fuel EU Enabled</Label>
            </div>
          </div>

          {/* EU Countries Only */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="euCountriesOnly"
              checked={filters.euCountriesOnly}
              onCheckedChange={(checked) => handleFilterChange('euCountriesOnly', checked)}
            />
            <Label htmlFor="euCountriesOnly" className="text-sm font-medium cursor-pointer">EU Countries Only</Label>
          </div>

          {/* No Region Filter */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="noRegion"
              checked={filters.noRegion}
              onCheckedChange={(checked) => handleFilterChange('noRegion', checked)}
            />
            <Label htmlFor="noRegion" className="text-sm font-medium cursor-pointer">Ports Without Region</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="showTimeZones"
              checked={filters.showTimeZones}
              onCheckedChange={(checked) => handleFilterChange('showTimeZones', checked)}
            />
            <Label htmlFor="showTimeZones" className="text-sm font-medium cursor-pointer">Show Time Zone Regions</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="showRegions"
              checked={filters.showRegions}
              onCheckedChange={(checked) => handleFilterChange('showRegions', checked)}
            />
            <Label htmlFor="showRegions" className="text-sm font-medium cursor-pointer">Show Maritime Regions</Label>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold">Continents</Label>
            <Input
              type="text"
              value={continentSearch}
              onChange={(e) => setContinentSearch(e.target.value)}
              placeholder="Search continents..."
              className="mb-2"
            />
            {filters.continents && filters.continents.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {filters.continents.map(contId => {
                  const continent = continents.find(c => c.id === contId);
                  return (
                    <Badge key={contId} variant="secondary" className="flex items-center gap-1">
                      {continent ? continent.name : contId}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => removeFromFilter('continents', contId)}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
            <div className="max-h-32 overflow-y-auto border rounded-md">
              {(continents || [])
                .filter(cont => cont && cont.id && cont.name && typeof cont.name === 'string')
                .filter(cont => !(filters.continents || []).includes(cont.id))
                .filter(cont => cont.name.toLowerCase().includes(continentSearch.toLowerCase()))
                .map(cont => (
                  <div
                    key={cont.id}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => {
                      addToFilter('continents', cont.id);
                      setContinentSearch('');
                    }}
                  >
                    {cont.name}
                  </div>
                ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Countries</Label>
            <Input
              type="text"
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              placeholder="Search countries..."
              className="mb-2"
            />
            {filters.countries && filters.countries.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {filters.countries.map(countryId => {
                  const country = (countriesData || []).find(c => c.id === countryId);
                  return country ? (
                    <Badge key={countryId} variant="secondary" className="flex items-center gap-1">
                      {country.name}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => removeFromFilter('countries', countryId)}
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            <div className="max-h-32 overflow-y-auto border rounded-md">
              {(countriesData || [])
                .filter(country => country && country.id)
                .filter(country => country.name && typeof country.name === 'string')
                .filter(country => !(filters.countries || []).includes(country.id))
                .filter(country => country.name.toLowerCase().includes(countrySearch.toLowerCase()))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(country => (
                  <div
                    key={country.id}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => {
                      addToFilter('countries', country.id);
                      setCountrySearch('');
                    }}
                  >
                    {country.name}
                  </div>
                ))}
            </div>
          </div>

          {/* Regions */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Regions</Label>
            <Input
              type="text"
              value={regionSearch}
              onChange={(e) => setRegionSearch(e.target.value)}
              placeholder="Search regions..."
              className="mb-2"
            />
            {filters.regions && filters.regions.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {filters.regions.map(regionId => {
                  const region = (regions || []).find(r => r.id === regionId);
                  return region ? (
                    <Badge key={regionId} variant="secondary" className="flex items-center gap-1">
                      {region.name}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => removeFromFilter('regions', regionId)}
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            <div className="max-h-32 overflow-y-auto border rounded-md">
              {(regions || [])
                .filter(region => region && region.id && region.name && typeof region.name === 'string')
                .filter(region => !(filters.regions || []).includes(region.id))
                .filter(region => region.name.toLowerCase().includes(regionSearch.toLowerCase()))
                .map(region => (
                  <div
                    key={region.id}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                    onClick={() => {
                      addToFilter('regions', region.id);
                      setRegionSearch('');
                    }}
                  >
                    {region.name}
                  </div>
                ))}
            </div>
          </div>

          {/* Time Zones */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Time Zones</Label>
            <Input
              type="text"
              value={timeZoneSearch}
              onChange={(e) => setTimeZoneSearch(e.target.value)}
              placeholder="Search time zones..."
              className="mb-2"
            />
            {filters.timeZones && filters.timeZones.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {filters.timeZones.map(tzId => {
                  const tz = (timeZonesData || []).find(t => t.id === tzId);
                  return tz ? (
                    <Badge key={tzId} variant="secondary" className="flex items-center gap-1 text-xs">
                      {tz.name}
                      <X 
                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                        onClick={() => removeFromFilter('timeZones', tzId)}
                      />
                    </Badge>
                  ) : null;
                })}
              </div>
            )}
            <div className="max-h-32 overflow-y-auto border rounded-md">
              {(() => {
                // Get unique time zone IDs from ports (numeric IDs)
                const uniqueTzIds = [...new Set((portsData || []).map(p => String(p.time_zone_id)).filter(Boolean))];
                
                if (!timeZonesData || timeZonesData.length === 0) {
                  return <div className="px-3 py-2 text-xs text-gray-400">Loading time zones...</div>;
                }
                
                const filteredTzs = (timeZonesData || [])
                  .filter(tz => tz.id && uniqueTzIds.includes(String(tz.id)))
                  .filter(tz => !(filters.timeZones || []).includes(tz.id))
                  .filter(tz => tz.name && tz.name.toLowerCase().includes(timeZoneSearch.toLowerCase()))
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                
                if (filteredTzs.length === 0) {
                  return <div className="px-3 py-2 text-xs text-gray-400">No time zones found</div>;
                }
                
                return filteredTzs.map(tz => (
                  <div
                    key={tz.id}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-xs"
                    onClick={() => {
                      addToFilter('timeZones', tz.id);
                      setTimeZoneSearch('');
                    }}
                  >
                    {tz.name}
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Reset Button */}
          <Button
            onClick={handleReset}
            variant="outline"
            className="w-full"
          >
            Reset Filters
          </Button>
        </div>
      </div>
    </div>
  );
});

export default FiltersPanel;
